"use client";

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  HStack,
  Icon,
  CloseButton,
  VStack,
  Box,
  Flex,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { supabase } from "../../supabaseClient";

interface SendInitialSMSDialogProps {
  isOpen: boolean;
  onClose: () => void;
  phoneNumber: string;
  agentId: string | null;
  initialMessage?: string;
}

export const SendInitialSMSDialog: React.FC<SendInitialSMSDialogProps> = ({
  isOpen,
  onClose,
  phoneNumber: _phoneNumber,
  agentId,
  initialMessage = "",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [toNumber, setToNumber] = useState("");
  const [dynamicVariables, setDynamicVariables] = useState<
    Record<string, string>
  >({});
  const toast = useToast();

  const modalContentBg = "white";
  const labelColor = "gray.700";
  const infoTextColor = "gray.600";

  const extractedVariables = useMemo(() => {
    const regex = /{{\s*([^{}]+?)\s*}}/g;
    const variables = new Set<string>();
    let match: RegExpExecArray | null = regex.exec(initialMessage);
    while (match) {
      const variableName = (match[1] || "").trim();
      if (variableName) variables.add(variableName);
      match = regex.exec(initialMessage);
    }
    return Array.from(variables);
  }, [initialMessage]);

  useEffect(() => {
    if (!isOpen) {
      setToNumber("");
      setDynamicVariables({});
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    if (!toNumber) {
      toast({
        title: "Phone number is required",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (!agentId) {
      toast({
        title: "No agent configured",
        description: "Please configure an agent for this phone number first",
        status: "error",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user) {
        throw new Error("No active session");
      }

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "user-id": session.user.id,
      };

      const response = await fetch(
        `${
          process.env.REACT_APP_SERVER_URL || ""
        }/api/sms/send-initial-message`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            phone_number: toNumber,
            agent_id: agentId,
            dynamic_vars_values: dynamicVariables,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to send initial SMS");
      }

      toast({
        title: "Initial SMS sent",
        description: `Message sent to ${toNumber}`,
        status: "success",
        duration: 3000,
      });
      setToNumber("");
      setDynamicVariables({});
      onClose();
    } catch (error: any) {
      toast({
        title: "Error sending initial SMS",
        description:
          error.message || "An error occurred while sending the message",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={modalContentBg} maxW="500px">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={4}
          color={labelColor}
        >
          Send Initial SMS
          <CloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <FormControl mb={4}>
            <FormLabel color={labelColor}>Phone Number</FormLabel>
            <Input
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              placeholder="e.g. +11234567890"
              borderColor={"gray.300"}
            />
          </FormControl>

          {extractedVariables.length > 0 && (
            <Box
              bg="gray.50"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p={3}
              mb={4}
            >
              <Text fontWeight="semibold" fontSize="sm" color={labelColor}>
                Dynamic Variables
              </Text>
              <Text fontSize="xs" color={infoTextColor} mt={1} mb={3}>
                Set dynamic variables for this initial SMS test
              </Text>

              <Flex mb={2} fontWeight="medium" fontSize="xs" color={labelColor}>
                <Box flex="1">Variable Name</Box>
                <Box flex="1">Test Value</Box>
              </Flex>

              <VStack spacing={2} align="stretch">
                {extractedVariables.map((variable) => (
                  <HStack key={variable} spacing={2}>
                    <Input
                      size="sm"
                      value={variable}
                      isReadOnly
                      fontSize="xs"
                      bg="white"
                    />
                    <Input
                      size="sm"
                      placeholder="Enter value"
                      value={dynamicVariables[variable] || ""}
                      fontSize="xs"
                      onChange={(e) =>
                        setDynamicVariables((prev) => ({
                          ...prev,
                          [variable]: e.target.value,
                        }))
                      }
                    />
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          <VStack
            spacing={2}
            color={infoTextColor}
            fontSize="sm"
            align={"start"}
          >
            <HStack spacing={2} color={infoTextColor}>
              <Icon as={InfoIcon} />
              <Text>The agent will send its initial greeting message.</Text>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button
            variant="ghost"
            colorScheme={"white"}
            textColor={"gray.800"}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            isDisabled={!agentId}
          >
            Send
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
